import React, { useCallback, useState } from "react";
import { omit } from "lodash";
import { notify } from "../../components/Notification";
import { ErrorState } from "../../components/States";
import { CreateFab } from "../../components/CreateFab";
import {
  useAddLiabilityPaymentMutation,
  useCreateRecurringTransactionMutation,
  useCreateTransactionMutation,
  useUpdateLiabilityPaymentMutation,
  useUpdateTransactionMutation,
} from "../../services";
import { Transaction } from "../../services/transactions/types";
import { Wrapper } from "./styles";
import { useWindowSize } from "../../hooks";
import {
  CreateTransactionsObject,
  TransactionsPanel,
  TransactionTypeOptions,
} from "../Transactions";
import { getTransactionObject } from "../Transactions/utils/transformers";
import { formatDate, TransactionType } from "../../utils";
import { useFinancesData } from "./useFinancesData";
import {
  HeroSection,
  TimelineSection,
  TrendAndTreemapSection,
  YourPlanSection,
  FinancesSkeleton,
  FinancesFirstRunState,
} from "./components";

export const Finances = () => {
  const {
    isLoading,
    isFirstRun,
    error,
    retry,
    cards,
    yoy,
    hasManualInvestmentAccounts,
    valuation,
    yearTotals,
    historyLoading,
    timelineSeries,
    timelineLabels,
    trendLoading,
    incomeTrendSeries,
    incomeTrendLabels,
    onFetchTrend,
    topExpensesLoading,
    topExpensesChartData,
    availableYears,
    recentTransactions,
    accounts,
    categoriesIncome,
    categoriesExpense,
  } = useFinancesData();

  const [transactionPanel, setTransactionPanel] = useState<{
    editingTransaction?: CreateTransactionsObject;
  } | null>(null);

  const [createTransaction] = useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [createRecurringTransaction] = useCreateRecurringTransactionMutation();
  const [addPayment] = useAddLiabilityPaymentMutation();
  const [updatePayment] = useUpdateLiabilityPaymentMutation();

  const { windowWidth } = useWindowSize();

  const handleOpenCreateTransaction = useCallback(() => {
    setTransactionPanel({});
  }, []);

  const handleOpenEditTransaction = useCallback((tx: Transaction) => {
    setTransactionPanel({ editingTransaction: getTransactionObject(tx) });
  }, []);

  const handleCloseTransactionPanel = useCallback(() => {
    setTransactionPanel(null);
  }, []);

  const handleSubmit = useCallback(
    async (data: CreateTransactionsObject): Promise<void> => {
      try {
        const transactionDate = formatDate(data.transactionDate);
        const payload: CreateTransactionsObject = {
          ...omit(data, ["id", "principalAmount", "interestAmount"]),
          transactionDate,
        };

        if (transactionPanel?.editingTransaction?.id) {
          if (
            data.transactionType.value === TransactionTypeOptions.LOAN_PAYMENT
          ) {
            const liabilityId = accounts.find(
              (account) => account.id === Number(data.toAccount?.value),
            )?.liabilityId;
            if (!liabilityId) {
              notify.error(
                "Liability account not found for this loan payment.",
              );
              return;
            }
            await updatePayment({
              id: liabilityId,
              paymentId: transactionPanel.editingTransaction.id,
              payload: {
                amount: Number.parseFloat(data.amount),
                principalAmount: Number.parseFloat(data.principalAmount!),
                paymentDate: transactionDate,
                fromAccountId: Number(data.fromAccount!.value),
                note: data.note,
              },
            }).unwrap();
          } else {
            await updateTransaction({
              transactionId: transactionPanel.editingTransaction.id,
              transaction: payload,
            }).unwrap();
          }
          notify.success("Transaction updated successfully!");
        } else {
          if (
            data.isRecurring &&
            (data.transactionType.value ===
              TransactionTypeOptions.LOAN_PAYMENT ||
              data.transactionType.value ===
                TransactionTypeOptions.MARKET_UPDATE)
          ) {
            notify.error(
              `Recurring ${data.transactionType.label} transactions are not supported!`,
            );
            return;
          }

          if (
            data.transactionType.value === TransactionTypeOptions.LOAN_PAYMENT
          ) {
            const liabilityId = accounts.find(
              (account) => account.id === Number(data.toAccount?.value),
            )?.liabilityId;
            const fromAccountId = Number(data.fromAccount!.value);
            if (liabilityId) {
              await addPayment({
                id: liabilityId,
                payload: {
                  amount: Number.parseFloat(data.amount),
                  principalAmount: Number.parseFloat(data.principalAmount!),
                  paymentDate: transactionDate,
                  fromAccountId,
                  note: data.note,
                },
              }).unwrap();
              notify.success("Transaction created successfully!");
            } else {
              notify.error("Server Error!");
            }
          } else {
            if (data.isRecurring) {
              await createRecurringTransaction({
                ...omit(payload, ["principalAmount", "interestAmount"]),
              }).unwrap();
            } else {
              await createTransaction({
                ...omit(payload, ["principalAmount", "interestAmount"]),
              }).unwrap();
            }
            notify.success("Transaction created successfully!");
          }
        }
        // Every write above is an RTK Query mutation carrying the transaction
        // ripple, so the dashboard's caches (recent transactions, cards, timeline,
        // net worth) refetch on their own.
        setTransactionPanel(null);
      } catch {
        // Global middleware shows the toast for mutation failures.
      }
    },
    [
      transactionPanel,
      accounts,
      addPayment,
      updatePayment,
      createTransaction,
      updateTransaction,
      createRecurringTransaction,
    ],
  );

  if (isLoading) {
    return <FinancesSkeleton />;
  }

  if (error) {
    return (
      <Wrapper>
        <ErrorState
          message="Unable to load your financial dashboard. Please try again."
          onRetry={retry}
        />
      </Wrapper>
    );
  }

  if (isFirstRun) {
    return <FinancesFirstRunState />;
  }

  return (
    <Wrapper>
      <HeroSection
        isLoading={isLoading}
        cards={cards}
        yoy={yoy}
        valuation={valuation}
        hasManualInvestmentAccounts={hasManualInvestmentAccounts}
      />

      <TimelineSection
        historyLoading={historyLoading}
        timelineSeries={timelineSeries}
        timelineLabels={timelineLabels}
        windowWidth={windowWidth}
        recentTransactions={recentTransactions}
        onCreateClick={handleOpenCreateTransaction}
        onTransactionClick={handleOpenEditTransaction}
      />

      <YourPlanSection
        yearTotals={yearTotals}
        valuation={valuation}
        income={cards.find((c) => c.id === "income")?.value || 0}
        expenses={cards.find((c) => c.id === "expenses")?.value || 0}
        investments={cards.find((c) => c.id === "investments")?.value ?? null}
        accounts={cards.find((c) => c.id === "accounts")?.value || 0}
        savings={cards.find((c) => c.id === "savings")?.value || 0}
        liabilities={cards.find((c) => c.id === "liabilities")?.value || 0}
        numberOfMonths={timelineLabels.length}
        hasManualInvestmentAccounts={hasManualInvestmentAccounts}
      />

      <TrendAndTreemapSection
        trendLoading={trendLoading}
        incomeTrendSeries={incomeTrendSeries}
        incomeTrendLabels={incomeTrendLabels}
        topExpensesLoading={topExpensesLoading}
        topExpensesChartData={topExpensesChartData}
        onFetchTrend={onFetchTrend}
        availableYears={availableYears}
        hasManualInvestmentAccounts={hasManualInvestmentAccounts}
      />

      {transactionPanel && (
        <TransactionsPanel
          title={
            transactionPanel.editingTransaction
              ? "Edit Transaction"
              : "Create Transaction"
          }
          action={transactionPanel.editingTransaction ? "edit" : "create"}
          isOpen
          incomeCategoryList={categoriesIncome}
          expenseCategoryList={categoriesExpense}
          onClose={handleCloseTransactionPanel}
          transaction={transactionPanel.editingTransaction}
          handleOnSubmit={handleSubmit}
          defaultAccount={null}
        />
      )}

      <CreateFab
        onClick={handleOpenCreateTransaction}
        ariaLabel="Add transaction"
      />
    </Wrapper>
  );
};
